package com.klantenstop.web.components.companyCard

import androidx.compose.runtime.Composable
import com.klantenstop.web.components.KsCategoryRow
import com.klantenstop.web.components.KsCompanyLogo
import com.klantenstop.web.components.KsExternalLink
import com.klantenstop.web.components.KsStatusUpdatedAt
import com.klantenstop.web.components.KsVerified
import com.klantenstop.web.components.toColor
import com.klantenstop.web.data.models.domain.Company
import com.klantenstop.web.navigation.Navigation
import com.klantenstop.web.styles.Typography
import com.klantenstop.web.utils.Constants
import com.klantenstop.web.utils.maxLines
import com.materialkobweb.components.widgets.DsCard
import com.materialkobweb.components.widgets.DsCardClickableStyle
import com.materialkobweb.components.widgets.DsMaterialSymbols
import com.materialkobweb.components.widgets.TextButtonLink
import com.materialkobweb.styles.MaterialColorVars
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.OverflowWrap
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.aspectRatio
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.columnGap
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.lineHeight
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.modifiers.maxWidth
import com.varabyte.kobweb.compose.ui.modifiers.overflowWrap
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.size
import com.varabyte.kobweb.silk.components.layout.SimpleGrid
import com.varabyte.kobweb.silk.components.layout.numColumns
import com.varabyte.kobweb.silk.components.navigation.Link
import com.varabyte.kobweb.silk.components.navigation.UndecoratedLinkVariant
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.breakpoint.rememberBreakpoint
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Text

@Composable
fun CompanyList(
    companies: List<Company>,
    modifier: Modifier = Modifier,
) {
    val breakpoint = rememberBreakpoint()
    val padding = if (breakpoint >= Breakpoint.MD) 36.px else 18.px

    SimpleGrid(
        modifier = modifier
            .fillMaxWidth(if (breakpoint >= Breakpoint.LG) 80.percent else 90.percent)
            .maxWidth(Constants.SECTION_WIDTH_2.px)
            .gap(rowGap = padding, columnGap = padding),
        numColumns = numColumns(base = 1, sm = 1, md = 2, lg = 2, xxl = 3)
    ) {
        companies.forEach { company ->
            KsCompanyCard(company = company)
        }
    }
}

@Composable
fun KsCompanyCard(
    company: Company,
    modifier: Modifier = Modifier
) {
    val breakpoint = rememberBreakpoint()
    val padding = if (breakpoint >= Breakpoint.MD) 24.px else 16.px

    Link(
        variant = UndecoratedLinkVariant,
        path = Navigation.Screen.CompanyDetail(companyIdentifier = company.publicIdentifier).route,
        modifier = modifier
    ) {
        DsCard(
            modifier = Modifier.fillMaxWidth(),
            variant = DsCardClickableStyle,
            contentPadding = padding
        ) {
            Column(
                modifier = Modifier.fillMaxWidth(),
                verticalArrangement = Arrangement.spacedBy(16.px)
            ) {
                // Header: Logo + Title + Verified Badge
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.spacedBy(16.px),
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    KsCompanyLogo(
                        company = company,
                        modifier = Modifier.size(if (breakpoint >= Breakpoint.MD) 80.px else 60.px).aspectRatio(1)
                    )

                    Column(
                        modifier = Modifier.fillMaxWidth(),
                        verticalArrangement = Arrangement.spacedBy(8.px)
                    ) {
                        SpanText(
                            modifier = Typography.TitleLarge.toModifier().maxLines(2).fillMaxWidth().overflowWrap(OverflowWrap.Anywhere),
                            text = company.name
                        )

                        if (company.verified) {
                            KsVerified()
                        }
                    }
                }

                // Categories
                if (company.categories.isNotEmpty()) {
                    KsCategoryRow(
                        categories = company.categories,
                        maxVisible = 3
                    )
                }

                // Description (truncated)
                if (company.description.isNotBlank()) {
                    SpanText(
                        modifier = Modifier
                            .fillMaxWidth()
                            .fontSize(14.px)
                            .lineHeight(20.px)
                            .color(MaterialColorVars.OnSurfaceVariant.value())
                            .overflowWrap(OverflowWrap.BreakWord)
                            .maxLines(2),
                        text = company.description
                    )
                }

                // Contact Info
                Column(
                    modifier = Modifier.fillMaxWidth(),
                    verticalArrangement = Arrangement.spacedBy(8.px)
                ) {
                    val contactModifier = Modifier
                        .columnGap(12.px)
                        .fontSize(14.px)
                        .lineHeight(16.px)

                    // Always show address
                    KsExternalLink(
                        modifier = contactModifier,
                        text = company.address?.fullAddress?.ifBlank { "/" } ?: "/",
                        href = company.address?.fullAddress?.let {
                            Navigation.External.Maps(it.replace(" ", "+").replace(",", "")).route
                        },
                        materialIcon = "location_on"
                    )

                    // Show phone if available
                    if (company.phoneNumber.isNotBlank()) {
                        KsExternalLink(
                            modifier = contactModifier,
                            text = company.phoneNumber,
                            href = Navigation.External.Phone(company.phoneNumber).route,
                            materialIcon = "phone"
                        )
                    }

                    // Show website if available
                    val websiteUrl = company.website?.href
                    if (websiteUrl != null) {
                        KsExternalLink(
                            modifier = contactModifier,
                            text = websiteUrl,
                            href = websiteUrl,
                            materialIcon = "globe"
                        )
                    }

                    // Show email address if available
                    val email = company.emailAddress.ifBlank { null }
                    if (email != null) {
                        KsExternalLink(
                            modifier = contactModifier,
                            text = email,
                            href = Navigation.External.Mail(email).route,
                            materialIcon = "mail"
                        )
                    }
                }

                // Status Visual
                Row(
                    modifier = Modifier
                        .margin(top = 12.px)
                        .fillMaxWidth()
                        .padding(12.px)
                        .backgroundColor(MaterialColorVars.SurfaceContainerLow.value())
                        .borderRadius(8.px),
                    verticalAlignment = Alignment.CenterVertically,
                    horizontalArrangement = Arrangement.spacedBy(12.px)
                ) {
                    DsMaterialSymbols(
                        modifier = Modifier.fontSize(20.px),
                        icon = company.status.status.toMaterialIcon(),
                        color = company.status.status.toColor()
                    )

                    Column(
                        modifier = Modifier.fillMaxWidth(),
                        verticalArrangement = Arrangement.spacedBy(4.px)
                    ) {
                        SpanText(
                            modifier = Modifier
                                .fontSize(14.px)
                                .lineHeight(16.px)
                                .fontWeight(FontWeight.SemiBold),
                            text = company.status.status.toText()
                        )

                        if (company.status.text.isNotBlank()) {
                            SpanText(
                                modifier = Modifier
                                    .fontSize(12.px)
                                    .lineHeight(16.px)
                                    .color(MaterialColorVars.OnSurfaceVariant.value())
                                    .overflowWrap(OverflowWrap.BreakWord)
                                    .maxLines(2),
                                text = company.status.text
                            )
                        }
                    }
                }

                // Footer: Status Updated + Action Button
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.SpaceBetween,
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    KsStatusUpdatedAt(
                        company = company
                    )

                    TextButtonLink(
                        path = Navigation.Screen.CompanyDetail(companyIdentifier = company.publicIdentifier).route
                    ) {
                        Text("Meer informatie")

                        DsMaterialSymbols(
                            modifier = Modifier
                                .fontSize(20.px)
                                .margin(left = 8.px),
                            icon = "arrow_forward"
                        )
                    }
                }
            }
        }
    }
}
